import { useSyncExternalStore } from 'react';
import type { EintragVerortung, InspectionItem } from '../../types';
import type { CaptureInput } from '../../services/bugReportService';
import type { KatalogAuswahl, MangelVorlage } from '../../services/mangelKatalog/typen';
import { hatPins } from '../../services/planPins';
import type { AssistentTyp } from './wizardKatalog';

/**
 * Reine Regeln des Handy-Assistenten (MobileInspectionWizard) neben dem
 * Standardmangel (der steht in wizardKatalog.ts): wann ein Eintrag als
 * unberührt gilt, der KI-Vorschlag (Laufnummer, Zeitgrenze, „Rückgängig“),
 * „wie zuvor“ in der Serienerfassung, Fotogrenze, Foto löschen in zwei Tipps,
 * der Stand des Speicherns und der lokale Entwurf (sichern, prüfen, fortsetzen).
 */

// ─── Schließen: unberührt oder nachfragen ────────────────────────────────────

/** Was beim Schließen zählt: Felder des Entwurfs plus der Zustand daneben. */
export interface EntwurfStand {
    /** Vorbefüllung (n.i.O.-Übergabe), solange sie nicht mit dem ersten Eintrag verbraucht ist. */
    vorbefuellt: boolean;
    fotos: number;
    daten: Pick<Partial<InspectionItem>, 'title' | 'observation' | 'location' | 'responsiblePerson' | 'regulation' | 'immediateAction' | 'verortung'>;
    hatPins: boolean;
    /** Frist selbst eingetragen. */
    deadlineTouched: boolean;
    /** Gewählter Mangel einer Freimeldung (auch nach Zurück aus „Mangel wählen“). */
    freimeldeZiel: boolean;
    /** Gewählter Standardmangel (auch „Freitext in …“ ohne Text). */
    katalogAuswahl: boolean;
}

/**
 * Nichts eingegeben — dieselbe Prüfung für X oben, „Abbrechen“ und die
 * Zurück-Taste im ersten Schritt. Zählt auch: selbst gesetzte Frist,
 * Sofortmaßnahme, gewählter Mangel einer Freimeldung, Standardmangel,
 * Vorschrift (KI/Katalog), Nadeln und Sensor-Verortung.
 */
export function entwurfUnberuehrt(s: EntwurfStand): boolean {
    const d = s.daten;
    return !s.vorbefuellt && s.fotos === 0
        && !d.title?.trim() && !d.observation?.trim() && !d.location?.trim()
        && !d.responsiblePerson?.trim() && !d.regulation?.trim()
        && !s.hatPins && !d.verortung
        && !s.deadlineTouched && !d.immediateAction
        && !s.freimeldeZiel && !s.katalogAuswahl;
}

/**
 * X, „Abbrechen“ oder Zurück-Taste im ersten Schritt: leer → schließen wie
 * bisher; sonst die Leiste „Eintrag verwerfen?“. Nach dem Speichern („done“)
 * steht nichts mehr in der Maske, das verloren gehen könnte.
 */
export function schliessenAktion(p: { schritt: string; unberuehrt: boolean }): 'schliessen' | 'nachfragen' {
    return p.schritt === 'done' || p.unberuehrt ? 'schliessen' : 'nachfragen';
}

// ─── KI-Vorschlag ────────────────────────────────────────────────────────────

/** Die Felder, die der Textvorschlag setzt — und die „Rückgängig“ wiederherstellt. */
export type KiFelder = Pick<Partial<InspectionItem>, 'title' | 'observation' | 'regulation'>;

export function kiFelderVon(d: KiFelder): KiFelder {
    return { title: d.title, observation: d.observation, regulation: d.regulation };
}

/** Vorschlag übernehmen: was die KI leer lässt, bleibt stehen. */
export function kiVorschlagAnwenden(
    bisher: KiFelder,
    vorschlag: { title: string; observation: string; regulation: string },
): KiFelder {
    return {
        title: vorschlag.title || bisher.title,
        observation: vorschlag.observation || bisher.observation,
        regulation: vorschlag.regulation || bisher.regulation,
    };
}

/** „Rückgängig“: genau die drei Felder von vorher zurück — auch ein leeres (undefined). */
export function kiRueckgaengigAnwenden<T extends KiFelder>(entwurf: T, vorher: KiFelder): T {
    return { ...entwurf, ...vorher };
}

/**
 * Gilt die Antwort des Vorschlags noch? Schrittwechsel, jede Eingabe in Titel
 * oder Beschreibung und „Abbrechen“ zählen die Laufnummer weiter — die späte
 * Antwort schreibt dann nichts mehr in die Maske (Tippen gewinnt).
 */
export function kiAntwortGilt(lauf: number, aktuell: number): boolean {
    return lauf === aktuell;
}

/**
 * So lange darf der Vorschlag dauern. Im Funkloch meldet das Handy oft weiter
 * „online“, der Abruf hängt dann ohne Antwort (und ein 504 wird bis zu dreimal
 * versucht) — danach gilt die KI als nicht erreichbar.
 */
export const KI_ZEITGRENZE_MS = 25_000;

/** Ergebnis von `mitZeitgrenze`, wenn die Zeit abgelaufen ist. */
export const ZEIT_ABGELAUFEN = Symbol('ki-zeitgrenze');

/**
 * Lässt `arbeit` gegen die Uhr laufen. Die Uhr löst mit ZEIT_ABGELAUFEN auf,
 * statt zu werfen (wie `mitZeitbudget` in freimeldungService.ts), und wird
 * immer abgeräumt. Die späte Antwort läuft im Hintergrund zu Ende und
 * interessiert dann niemanden mehr.
 */
export async function mitZeitgrenze<T>(arbeit: Promise<T>, ms: number): Promise<T | typeof ZEIT_ABGELAUFEN> {
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const wecker = new Promise<typeof ZEIT_ABGELAUFEN>((resolve) => {
        uhr = setTimeout(() => resolve(ZEIT_ABGELAUFEN), ms);
    });
    try {
        return await Promise.race([arbeit, wecker]);
    } finally {
        if (uhr !== undefined) clearTimeout(uhr);
    }
}

/**
 * KI-Knopf beim Mangel und beim Hinweis: der Proxy formuliert einen
 * Mängeltext — für Positiv, Information und Freimeldung passt er nicht.
 */
export function kiFuerTyp(typ: AssistentTyp): boolean {
    return typ === 'defect' || typ === 'info';
}

const netzAbonnieren = (melden: () => void): (() => void) => {
    window.addEventListener('online', melden);
    window.addEventListener('offline', melden);
    return () => {
        window.removeEventListener('online', melden);
        window.removeEventListener('offline', melden);
    };
};

/** Nur ein ausdrückliches „offline“ des Browsers zählt; ohne Angabe gilt das Netz als da. */
export const netzDa = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;

/** KI-Knopf: bei `navigator.onLine === false` gesperrt („KI braucht Netz“), folgt online/offline. */
export const useNetzDa = (): boolean => useSyncExternalStore(netzAbonnieren, netzDa, netzDa);

// ─── Serienerfassung: „wie zuvor“ ────────────────────────────────────────────

/** Ort und Firma des zuletzt gespeicherten Eintrags. */
export interface VorigeAngaben {
    ort: string;
    firma: string;
}

export const KEINE_VORIGEN_ANGABEN: VorigeAngaben = { ort: '', firma: '' };

/** Nach dem Speichern merken — ein leerer Wert (z. B. Information) überschreibt nichts. */
export function vorigeAngabenNach(
    bisher: VorigeAngaben,
    eintrag: Pick<Partial<InspectionItem>, 'location' | 'responsiblePerson'>,
): VorigeAngaben {
    return {
        ort: eintrag.location?.trim() || bisher.ort,
        firma: eintrag.responsiblePerson?.trim() || bisher.firma,
    };
}

/**
 * Chip „wie zuvor: …“ in „Wer“/„Wo“: nichts wird still vorbelegt, ein Tipp
 * übernimmt. Nur mit vorigem Wert und nur, wenn er nicht schon eingetragen ist.
 */
export function wieZuvor(vorig: string, aktuell: string | undefined): string | null {
    const v = vorig.trim();
    return v && v !== (aktuell ?? '').trim() ? v : null;
}

// ─── Fotos ───────────────────────────────────────────────────────────────────

/** Gezielte Freimeldung: übernommen wird nur das erste Foto (closedImage) — also nur eines zulassen. */
export function fotoGrenze(gezielteFreimeldung: boolean, max: number): number {
    return gezielteFreimeldung ? 1 : max;
}

/** Zeile unter den Fotos. Stammen mehr Fotos aus einem früheren Typ, sagt sie, welches zählt. */
export function fotoZaehlerText(anzahl: number, grenze: number, gezielteFreimeldung: boolean): string {
    if (!gezielteFreimeldung) return `${anzahl} von ${grenze} Bildern`;
    return anzahl > 1 ? `1 Beleg-Foto – übernommen wird nur das erste von ${anzahl}` : `${anzahl} von 1 Beleg-Foto`;
}

/** Grenze als Satzanfang für die Meldung bei zu vielen Bildern. */
export function fotoGrenzeText(grenze: number, gezielteFreimeldung: boolean): string {
    return gezielteFreimeldung ? 'Nur 1 Beleg-Foto bei der Freimeldung' : `Maximal ${grenze} Bilder pro Eintrag`;
}

/** So lange steht „Wirklich löschen?“ am Foto, dann gilt der erste Tipp als verfallen. */
export const FOTO_LOESCHEN_FRIST_MS = 3000;

/**
 * „Fertig“ (bzw. X, Zurück, „Verwerfen“) waehrend noch gespeichert wird: der
 * Assistent wartet und schliesst danach von selbst. Nach dieser Frist gibt es
 * einen Notausgang (dann kommt das Ergebnis auf dem spaeten Weg an).
 */
export const SCHLIESSEN_NOTAUSGANG_MS = 45_000;

/**
 * Darf der Assistent jetzt zu? Nur ohne laufende Speicherung und ohne
 * unquittierten Fehlschlag — sonst schlösse er ueber dem roten Balken
 * „Nicht gespeichert“, und der Hinweis ginge mit ihm.
 */
export const darfSchliessen = (laufendeSpeicherungen: number, nichtGespeichert: number): boolean =>
    laufendeSpeicherungen === 0 && nichtGespeichert === 0;

/** Foto löschen in zwei Tipps: der erste fragt nach, erst der zweite auf DASSELBE Foto löscht. */
export function fotoLoeschTipp(gefragt: number | null, index: number): { loeschen: boolean; gefragt: number | null } {
    return gefragt === index ? { loeschen: true, gefragt: null } : { loeschen: false, gefragt: index };
}

// ─── Speicherstand im Schritt „done“ ─────────────────────────────────────────

/**
 * 'erfasst' — übergeben, der Aufrufer meldet nichts zurück;
 * 'laeuft'  — seine Zusage steht noch aus (Foto-Upload, dann Schreiben);
 * 'fertig' / 'fehler' — sie ist erfüllt bzw. gescheitert.
 */
export type SpeicherStand = 'erfasst' | 'laeuft' | 'fertig' | 'fehler';

export function istZusage(x: unknown): x is PromiseLike<unknown> {
    return !!x && typeof (x as { then?: unknown }).then === 'function';
}

/** Kopfzeile und Text: „Gespeichert“ erst, wenn es stimmt. */
export function speicherText(stand: SpeicherStand): { kopf: string; text: string } {
    if (stand === 'fertig') return { kopf: 'Gespeichert', text: 'Eintrag im Bericht gespeichert' };
    if (stand === 'fehler') return { kopf: 'Nicht gespeichert', text: 'Speichern fehlgeschlagen – bitte erneut erfassen' };
    return { kopf: 'Erfasst', text: 'Erfasst – wird gespeichert' };
}

/**
 * Stand in „done“ nach dem Ausgang einer Zusage — nur für den zuletzt
 * gespeicherten Eintrag (`null`: Stand bleibt). Die späte Zusage eines
 * früheren Eintrags überschreibt den Stand des neueren nicht; scheitert sie,
 * meldet sie der Balken „Nicht gespeichert“ trotzdem.
 */
export function speicherStandNach(lauf: number, aktuell: number, ausgang: 'fertig' | 'fehler'): SpeicherStand | null {
    return lauf === aktuell ? ausgang : null;
}

/**
 * Roter Balken unter dem Kopf: jede gescheiterte Zusage, unabhängig von
 * Schritt und Laufnummer, bis der Nutzer ihn wegtippt. `null`: nichts offen.
 */
export function nichtGespeichertText(eintraege: string[]): string | null {
    if (eintraege.length === 0) return null;
    return `Nicht gespeichert: ${eintraege.map(t => `„${t.trim() || 'Eintrag'}“`).join(', ')} – bitte erneut erfassen.`;
}

/** Bug-Report zur gescheiterten Zusage — früher kam sie als unbehandelte Ablehnung dort an. */
export function speicherFehlerBericht(err: unknown): CaptureInput {
    return {
        message: `Assistent: Eintrag nicht gespeichert – ${err instanceof Error ? err.message : String(err)}`,
        stack: err instanceof Error ? err.stack : undefined,
        source: 'rejection',
    };
}

// ─── Lokaler Entwurf: sichern, prüfen, fortsetzen ────────────────────────────
//
// Android beendet die App gern, während die Systemkamera offen ist
// (Prozess-Aus), und eine neu geladene Seite fängt von vorn an. Der Assistent
// legt seinen Stand deshalb als Entwurf ab (services/assistentEntwurf.ts) und
// bietet ihn beim nächsten Öffnen an. Hier stehen die Regeln: was in den
// Entwurf kommt, wann ein gelesener gilt, wem der Speicher in einer Sitzung
// gehört und wo das Fortsetzen landet.

/** Schritte des Assistenten (gleich `Step` im Wizard). */
export type AssistentSchritt = 'type' | 'katalog' | 'pick' | 'what' | 'who' | 'where' | 'finish' | 'done';

/** Fassung der gespeicherten Form. Eine neue Form zählt hoch — ältere Entwürfe gelten dann nicht mehr. */
export const ENTWURF_FORMAT = 1;

/** Ruhe nach der letzten Änderung, bevor gesichert wird (vor Kamera und Album: sofort). */
export const ENTWURF_ENTPRELLEN_MS = 400;

/** Älter als einen Tag: verworfen — die Begehung ist dann vorbei, der Stand passt nicht mehr. */
export const ENTWURF_HOECHSTALTER_MS = 24 * 60 * 60 * 1000;

/** Uhr des Geräts zurückgestellt: so weit „in der Zukunft“ gilt ein Entwurf noch. */
const ENTWURF_UHR_SPIELRAUM_MS = 5 * 60 * 1000;

/** Felder des Eintrags im Entwurf. Den Status trägt er nicht — der folgt dem Typ (`statusFuerTyp`). */
export type EntwurfDaten = Pick<Partial<InspectionItem>,
    | 'title' | 'observation' | 'location' | 'responsiblePerson' | 'regulation' | 'deadline' | 'immediateAction'
    | 'planId' | 'planCoordinates' | 'verortung' | 'katalogKey' | 'katalogKategorie' | 'sectionId'>;

/** Gespeicherter Stand des Assistenten. Fotos als Base64-Text wie in der Maske. */
export interface AssistentEntwurf {
    format: typeof ENTWURF_FORMAT;
    /** Zeitpunkt der Sicherung (ms seit 1970). */
    gespeichertAm: number;
    typ: AssistentTyp;
    /** Schritt beim Sichern — nie 'done' (dann ist der Eintrag übergeben). */
    schritt: AssistentSchritt;
    /** Weg beim Mangel: Standardmangel (Torte) oder eigener Text. */
    mitKatalog: boolean;
    daten: EntwurfDaten;
    fotos: string[];
    /** Gewählter Mangel einer Freimeldung — nur die id; beim Fortsetzen neu aus den offenen Mängeln aufgelöst. */
    freimeldeZielId: string | null;
    /** Frist selbst eingetragen (bleibt beim Typwechsel stehen). */
    deadlineTouched: boolean;
    /** Gewählter Standardmangel (Chip in „Details“). */
    katalogAuswahl: KatalogAuswahl | null;
}

/** Was der Assistent hält und in den Entwurf gibt. */
export interface EntwurfZustand {
    typ: AssistentTyp;
    schritt: AssistentSchritt;
    mitKatalog: boolean;
    daten: Partial<InspectionItem>;
    fotos: readonly string[];
    freimeldeZielId: string | null;
    deadlineTouched: boolean;
    katalogAuswahl: KatalogAuswahl | null;
}

/** Status je Eintragstyp — dieselbe Zuordnung für die Typwahl und das Fortsetzen eines Entwurfs. */
export function statusFuerTyp(typ: AssistentTyp): InspectionItem['status'] {
    switch (typ) {
        case 'info': return 'info';
        case 'good': return 'good';
        case 'clearance': return 'closed';
        case 'general': return 'general';
        default: return 'open';
    }
}

const ASSISTENT_TYPEN: readonly AssistentTyp[] = ['defect', 'info', 'good', 'clearance', 'general'];
/** 'done' fehlt mit Absicht: nach der Übergabe gibt es keinen Entwurf mehr. */
const ENTWURF_SCHRITTE: readonly AssistentSchritt[] = ['type', 'katalog', 'pick', 'what', 'who', 'where', 'finish'];
const KATALOG_ARTEN: readonly KatalogAuswahl['art'][] = ['knoten', 'eintrag', 'freitext'];
const DRINGLICHKEITEN: readonly NonNullable<MangelVorlage['dringlichkeit']>[] = ['sofort', 'frist'];

const istObjekt = (wert: unknown): wert is Record<string, unknown> =>
    typeof wert === 'object' && wert !== null && !Array.isArray(wert);
const istAssistentTyp = (wert: unknown): wert is AssistentTyp =>
    typeof wert === 'string' && (ASSISTENT_TYPEN as readonly string[]).includes(wert);
const istEntwurfSchritt = (wert: unknown): wert is AssistentSchritt =>
    typeof wert === 'string' && (ENTWURF_SCHRITTE as readonly string[]).includes(wert);
const textOder = (wert: unknown, sonst: string): string => (typeof wert === 'string' ? wert : sonst);
/** Text, ausdrücklich null — oder undefined (fehlt bzw. falsche Art). */
const textOderNull = (wert: unknown): string | null | undefined => {
    if (typeof wert === 'string') return wert;
    return wert === null ? null : undefined;
};
const zahlOderNull = (wert: unknown): number | null | undefined => {
    if (typeof wert === 'number' && Number.isFinite(wert)) return wert;
    return wert === null ? null : undefined;
};

/** Sensor-Verortung aus einem gelesenen Entwurf — nur die bekannten Felder, jedes in seiner Art. */
function verortungVon(roh: unknown): EintragVerortung | undefined {
    if (!istObjekt(roh) || roh.quelle !== 'sensor') return undefined;
    const v: EintragVerortung = { quelle: 'sensor' };
    const ebene = textOderNull(roh.ebene);
    if (ebene !== undefined) v.ebene = ebene;
    for (const feld of ['hoeheM', 'genauigkeitM', 'blickGradPlan', 'sicherheit'] as const) {
        const zahl = zahlOderNull(roh[feld]);
        if (zahl !== undefined) v[feld] = zahl;
    }
    return v;
}

/**
 * Die Felder, die ein Entwurf trägt — aus dem Zustand ebenso wie aus einem
 * gelesenen (ungeprüften) Entwurf. Was nicht die erwartete Art hat, fällt weg;
 * die vier Eingabefelder und die Frist sind immer Text (die Maske braucht
 * kontrollierte Felder).
 */
function entwurfDatenVon(quelle: object): EntwurfDaten {
    const roh = quelle as Record<string, unknown>;
    const daten: EntwurfDaten = {
        title: textOder(roh.title, ''),
        observation: textOder(roh.observation, ''),
        location: textOder(roh.location, ''),
        responsiblePerson: textOder(roh.responsiblePerson, ''),
        deadline: textOder(roh.deadline, ''),
    };
    if (typeof roh.regulation === 'string') daten.regulation = roh.regulation;
    if (roh.immediateAction === true) daten.immediateAction = true;
    if (typeof roh.planId === 'string' && roh.planId) daten.planId = roh.planId;
    // Nadeln: pinsVon (planPins.ts) liest jede Form und lässt Ungültiges fallen.
    if (istObjekt(roh.planCoordinates) || Array.isArray(roh.planCoordinates)) {
        daten.planCoordinates = roh.planCoordinates as InspectionItem['planCoordinates'];
    }
    const verortung = verortungVon(roh.verortung);
    if (verortung) daten.verortung = verortung;
    const katalogKey = textOderNull(roh.katalogKey);
    if (katalogKey !== undefined) daten.katalogKey = katalogKey;
    const katalogKategorie = textOderNull(roh.katalogKategorie);
    if (katalogKategorie !== undefined) daten.katalogKategorie = katalogKategorie;
    if (typeof roh.sectionId === 'string' && roh.sectionId) daten.sectionId = roh.sectionId;
    return daten;
}

/** Verknüpfung zum Katalog nur zusammen mit dem Chip — sonst hinge am Eintrag ein Bezug, den niemand sieht. */
function katalogFelderAbgleichen(daten: EntwurfDaten, auswahl: KatalogAuswahl | null): EntwurfDaten {
    if (auswahl) return daten;
    const ohne: EntwurfDaten = { ...daten };
    delete ohne.katalogKey;
    delete ohne.katalogKategorie;
    return ohne;
}

/** Gewählter Standardmangel aus einem gelesenen Entwurf; unlesbar → null (Chip fällt weg). */
function katalogAuswahlVon(roh: unknown): KatalogAuswahl | null {
    if (!istObjekt(roh)) return null;
    const art = KATALOG_ARTEN.find(a => a === roh.art);
    if (!art) return null;
    if (!Array.isArray(roh.pfad) || !roh.pfad.every(t => typeof t === 'string')) return null;
    if (typeof roh.pikto !== 'string') return null;
    const katalogKey = textOderNull(roh.katalogKey);
    const katalogKategorie = textOderNull(roh.katalogKategorie);
    const v = roh.vorlage;
    // Die Vorlage braucht der Assistent nach der Wahl nicht mehr (nur der Chip zählt) — unlesbar heisst: keine.
    let vorlage: MangelVorlage | null = null;
    if (istObjekt(v) && typeof v.titel === 'string' && typeof v.text === 'string'
        && typeof v.vorschrift === 'string' && typeof v.platzhalter === 'boolean') {
        vorlage = { titel: v.titel, text: v.text, vorschrift: v.vorschrift, platzhalter: v.platzhalter };
        if (typeof v.eintragId === 'string') vorlage.eintragId = v.eintragId;
        const dringlichkeit = DRINGLICHKEITEN.find(d => d === v.dringlichkeit);
        if (dringlichkeit) vorlage.dringlichkeit = dringlichkeit;
        if (typeof v.fristTage === 'number' && Number.isFinite(v.fristTage)) vorlage.fristTage = v.fristTage;
    }
    const auswahl: KatalogAuswahl = {
        art,
        katalogKey: katalogKey ?? null,
        katalogKategorie: katalogKategorie ?? null,
        vorlage,
        pfad: roh.pfad.filter((t): t is string => typeof t === 'string'),
        pikto: roh.pikto,
    };
    if (typeof roh.anzahl === 'number' && Number.isFinite(roh.anzahl)) auswahl.anzahl = roh.anzahl;
    return auswahl;
}

/** Entwurf aus dem Zustand des Assistenten. `jetzt` kommt vom Aufrufer (nie im Render). */
export function entwurfAusZustand(z: EntwurfZustand, jetzt: number): AssistentEntwurf {
    // Standardmangel nur beim Mangel, das Freimelde-Ziel nur bei der Freimeldung (wie die Typwahl).
    const katalogAuswahl = z.typ === 'defect' ? z.katalogAuswahl : null;
    return {
        format: ENTWURF_FORMAT,
        gespeichertAm: jetzt,
        typ: z.typ,
        schritt: z.schritt,
        mitKatalog: z.mitKatalog,
        daten: katalogFelderAbgleichen(entwurfDatenVon(z.daten), katalogAuswahl),
        fotos: [...z.fotos],
        freimeldeZielId: z.typ === 'clearance' ? z.freimeldeZielId : null,
        deadlineTouched: z.deadlineTouched,
        katalogAuswahl,
    };
}

/**
 * Sichern oder löschen? Gesichert wird nur, was jemand eingegeben hat und noch
 * nicht übergeben ist. Unberührt → löschen (nichts zu bewahren); 'done' →
 * löschen (der Eintrag ist im Speicherweg und darf nie als Entwurf wiederkommen).
 */
export function entwurfAktion(p: { unberuehrt: boolean; schritt: string }): 'sichern' | 'loeschen' {
    return p.unberuehrt || p.schritt === 'done' ? 'loeschen' : 'sichern';
}

/** Ein Entwurf als Stand für `entwurfUnberuehrt` — ein leerer Entwurf ist keiner. */
function entwurfStand(e: Pick<AssistentEntwurf, 'daten' | 'fotos' | 'deadlineTouched' | 'freimeldeZielId' | 'katalogAuswahl'>): EntwurfStand {
    return {
        vorbefuellt: false,
        fotos: e.fotos.length,
        daten: e.daten,
        hatPins: hatPins(e.daten),
        deadlineTouched: e.deadlineTouched,
        freimeldeZiel: !!e.freimeldeZielId,
        katalogAuswahl: !!e.katalogAuswahl,
    };
}

/**
 * Gelesenen Entwurf prüfen. `null` — verwerfen: keine erkennbare Form, andere
 * Fassung, älter als ENTWURF_HOECHSTALTER_MS (oder deutlich „aus der Zukunft“),
 * unbekannter Typ oder Schritt, oder nichts darin, das sich zu bewahren lohnt.
 * Einzelne unlesbare Felder fallen weg, statt den Entwurf zu kippen.
 */
export function entwurfPruefen(roh: unknown, jetzt: number): AssistentEntwurf | null {
    if (!istObjekt(roh) || roh.format !== ENTWURF_FORMAT) return null;
    const am = roh.gespeichertAm;
    if (typeof am !== 'number' || !Number.isFinite(am)) return null;
    const alter = jetzt - am;
    if (alter > ENTWURF_HOECHSTALTER_MS || alter < -ENTWURF_UHR_SPIELRAUM_MS) return null;
    const typ = roh.typ;
    const schritt = roh.schritt;
    if (!istAssistentTyp(typ) || !istEntwurfSchritt(schritt)) return null;
    if (!istObjekt(roh.daten) || !Array.isArray(roh.fotos)) return null;
    const katalogAuswahl = typ === 'defect' ? katalogAuswahlVon(roh.katalogAuswahl) : null;
    const entwurf: AssistentEntwurf = {
        format: ENTWURF_FORMAT,
        gespeichertAm: am,
        typ,
        schritt,
        mitKatalog: typeof roh.mitKatalog === 'boolean' ? roh.mitKatalog : true,
        daten: katalogFelderAbgleichen(entwurfDatenVon(roh.daten), katalogAuswahl),
        fotos: roh.fotos.filter((f): f is string => typeof f === 'string' && f.length > 0),
        freimeldeZielId: typ === 'clearance' && typeof roh.freimeldeZielId === 'string' && roh.freimeldeZielId
            ? roh.freimeldeZielId
            : null,
        deadlineTouched: roh.deadlineTouched === true,
        katalogAuswahl,
    };
    return entwurfUnberuehrt(entwurfStand(entwurf)) ? null : entwurf;
}

/**
 * Wem gehört der Speicher in dieser Sitzung?
 *  'aus'      — kein Schlüssel (ohne Projekt): nichts sichern, nichts lesen;
 *  'pruefen'  — beim Öffnen wird gelesen: noch nichts schreiben, sonst
 *               überschriebe die neue Sitzung den alten Entwurf ungefragt;
 *  'angebot'  — Leiste „Nicht gespeicherter Eintrag“ steht, bis entschieden ist;
 *  'frei'     — der Speicher gehört dieser Sitzung: sichern, löschen;
 *  'gesperrt' — ein fremder Entwurf liegt und wird nicht angeboten
 *               (Vorbefüllung, oder die Maske war schon in Arbeit): diese
 *               Sitzung lässt ihn ganz in Ruhe — er kommt beim nächsten Öffnen.
 */
export type EntwurfPhase = 'aus' | 'pruefen' | 'angebot' | 'frei' | 'gesperrt';

/**
 * Phase nach dem Lesen beim Öffnen. Angeboten wird nur ohne Vorbefüllung
 * (n.i.O. aus der Checkliste, gezielte Flüsse) und nur, solange die Maske
 * noch leer im Schritt „Typ“ steht. Sonst bleibt der Entwurf liegen — auch
 * nicht überschrieben: genau ein Entwurf je Schlüssel hieße sonst, ihn still
 * zu löschen.
 */
export function phaseNachLaden(p: { entwurf: AssistentEntwurf | null; vorbefuellt: boolean; maskeUnberuehrt: boolean }): 'angebot' | 'frei' | 'gesperrt' {
    if (!p.entwurf) return 'frei';
    return p.vorbefuellt || !p.maskeUnberuehrt ? 'gesperrt' : 'angebot';
}

/** Schritte, in denen das Fortsetzen landen kann. */
export type FortsetzSchritt = 'pick' | 'what' | 'who' | 'where';

/**
 * Sicherer Schritt beim Fortsetzen:
 *  - 'katalog' (Torte) braucht ihre Laufzeitsteuerung → „Details“; der Text
 *    steht dort, „Zurück“ öffnet die Torte wie gewohnt; 'finish' → „Wo“.
 *  - Information speichert in „Details“ — dorthin, gleich wo sie stand.
 *  - 'type' → „Details“ des gesicherten Typs: wer fortsetzt, will seine
 *    Eingabe sehen; die Typwahl liegt einen Tipp dahinter.
 *  - Freimeldung mit Ziel: Ziel wieder da → „Details“ (gezielt). Fehlt es
 *    (inzwischen geschlossen, entfernt oder nicht in diesem Bericht) →
 *    „Mangel wählen“, wenn es eine Auswahl gibt — still eine bezugslose
 *    „Erledigung“ daraus zu machen, legte einen anderen Eintrag an als den
 *    erfassten (einen neuen statt des geschlossenen Mangels). Ohne Auswahl
 *    bleibt nur der alte Weg: bezugslos in „Details“.
 *  - Freimeldung ohne Ziel in 'pick'/'type' → „Mangel wählen“, wenn möglich.
 */
export function fortsetzSchritt(
    e: Pick<AssistentEntwurf, 'typ' | 'schritt' | 'freimeldeZielId'>,
    p: { zielGefunden: boolean; auswahlMoeglich: boolean },
): FortsetzSchritt {
    if (e.typ === 'clearance' && e.freimeldeZielId) {
        if (p.zielGefunden) return 'what';
        return p.auswahlMoeglich ? 'pick' : 'what';
    }
    if (e.typ === 'general') return 'what';
    switch (e.schritt) {
        case 'who':
        case 'where':
            return e.schritt;
        case 'finish':
            return 'where';
        case 'pick':
        case 'type':
            return e.typ === 'clearance' && p.auswahlMoeglich ? 'pick' : 'what';
        default:
            return 'what';
    }
}

/** Was „Fortsetzen“ in den Assistenten zurückschreibt. */
export interface Wiederherstellung {
    typ: AssistentTyp;
    schritt: FortsetzSchritt;
    mitKatalog: boolean;
    daten: Partial<InspectionItem>;
    fotos: string[];
    /** Aus den aktuellen offenen Mängeln aufgelöst (frischer Titel, Nummer); null, wenn es ihn nicht mehr gibt. */
    freimeldeZiel: InspectionItem | null;
    deadlineTouched: boolean;
    katalogAuswahl: KatalogAuswahl | null;
}

/**
 * „Fortsetzen“: Typ, Felder, Fotos, Frist-Merker, Standardmangel zurück; den
 * Schritt wählt `fortsetzSchritt`, das Freimelde-Ziel kommt per id aus
 * `offeneMaengel` (nur mit Auswahl — ohne `onCloseItem` liesse es sich gar
 * nicht schliessen). Der Status folgt dem Typ.
 */
export function entwurfWiederherstellen(
    e: AssistentEntwurf,
    p: { offeneMaengel: readonly InspectionItem[]; freimeldungMitAuswahl: boolean },
): Wiederherstellung {
    const ziel = e.typ === 'clearance' && e.freimeldeZielId && p.freimeldungMitAuswahl
        ? p.offeneMaengel.find(i => i.id === e.freimeldeZielId) ?? null
        : null;
    const auswahlMoeglich = p.freimeldungMitAuswahl && p.offeneMaengel.length > 0;
    return {
        typ: e.typ,
        schritt: fortsetzSchritt(e, { zielGefunden: !!ziel, auswahlMoeglich }),
        mitKatalog: e.mitKatalog,
        daten: { ...e.daten, status: statusFuerTyp(e.typ) },
        fotos: [...e.fotos],
        freimeldeZiel: ziel,
        deadlineTouched: e.deadlineTouched,
        katalogAuswahl: e.typ === 'defect' ? e.katalogAuswahl : null,
    };
}

const zweistellig = (n: number): string => String(n).padStart(2, '0');
const gleicherTag = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Zeit für die Leiste „Nicht gespeicherter Eintrag von …“: „14:05“, „gestern, 23:10“ (Ortszeit des Geräts). */
export function entwurfZeitText(gespeichertAm: number, jetzt: number): string {
    const am = new Date(gespeichertAm);
    const uhrzeit = `${zweistellig(am.getHours())}:${zweistellig(am.getMinutes())}`;
    const heute = new Date(jetzt);
    if (gleicherTag(am, heute)) return uhrzeit;
    if (gleicherTag(am, new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - 1))) return `gestern, ${uhrzeit}`;
    return `${zweistellig(am.getDate())}.${zweistellig(am.getMonth() + 1)}., ${uhrzeit}`;
}

const TYP_NAMEN: Record<AssistentTyp, string> = {
    defect: 'Mangel', info: 'Hinweis', good: 'Positiv', clearance: 'Freimeldung', general: 'Information',
};
const TITEL_HOECHSTENS = 50;

/** Zweite Zeile der Leiste: was im Entwurf steckt, z. B. „Mangel · „Geländer fehlt“ · 2 Fotos“. */
export function entwurfBeschreibung(e: Pick<AssistentEntwurf, 'typ' | 'daten' | 'fotos'>): string {
    const titel = (e.daten.title ?? '').trim();
    const teile = [TYP_NAMEN[e.typ]];
    if (titel) teile.push(`„${titel.length > TITEL_HOECHSTENS ? `${titel.slice(0, TITEL_HOECHSTENS - 1).trimEnd()}…` : titel}“`);
    if (e.fotos.length > 0) teile.push(e.fotos.length === 1 ? '1 Foto' : `${e.fotos.length} Fotos`);
    return teile.join(' · ');
}
